import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	AfterViewInit,
	OnChanges,
	Output,
	OnInit,
} from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { ExtendedPhoneNumber, PhoneNumber } from 'src/app/models/data/user';
import { GeoService } from '../../services/geo.service';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { CountryAvailableSms } from 'src/app/models/model';
import { MatFormField, MatError } from '@angular/material/form-field';
import { NgxFlagPickerComponent } from '../ngx-flag-picker/ngx-flag-picker.component';
import { NgClass } from '@angular/common';
import { MatInput } from '@angular/material/input';

@Component({
	selector: 'app-contact-phone-type-form',
	templateUrl: './phone-type-form.component.html',
	styleUrls: [
		'./phone-type-form.component.scss',
		'../../ui/common/main.scss',
		'../../ui/common/stepper.scss',
	],
	imports: [
		FormsModule,
		ReactiveFormsModule,
		MatFormField,
		NgxFlagPickerComponent,
		NgClass,
		MatInput,
		MatError,
	],
})
export class PhoneTypeFormComponent implements OnChanges, OnInit {
	phoneTypeFormGroup!: UntypedFormGroup;

	@Input() phoneType!: string;
	@Input() isDisabled!: boolean;
	@Input() existingPhoneNumber: PhoneNumber | undefined;
	@Input() optional: boolean = false;
	@Input() flagForDisplayErrorMessage: boolean = false;

	@Output() userPhoneNumber = new EventEmitter<
		PhoneNumber | ExtendedPhoneNumber
	>();

	readonly optionalPlaceholder = $localize`:@@deviceGenerateOneDayCode-optional:Optional`;
	readonly mobileNumberPlaceholder = $localize`:@@userSearchBar-MobileNumberPlaceholder:Mobile Number`;

	phoneNumber = '';
	selectedCountryCode = '';
	phoneCode = '';
	countryAvailableSms?: CountryAvailableSms;
	// This countryCodes is the one that works with ngx-flag-picker
	// IMPORTANT: must be lowercase for flags to properly show
	countryCodes: string[] = [];

	constructor(
		private fb: UntypedFormBuilder,
		private geoService: GeoService,
		private readonly apiService: ApiService,
		private changeDetector: ChangeDetectorRef
	) {
		this.initForm();
	}

	// TODO: try switching this to on init
	async ngOnInit(): Promise<void> {
		// this will get the available country codes from the server
		await this.loadAvailableCountryCodes();

		// do not change selected country code if its already set. This was causing issues from Device/Generate Code Screen
		if (!this.selectedCountryCode) {
			// this is the country to select by default
			await this.changeSelectedCountryCode(
				this.countryAvailableSms?.activeCompanyCountryCode
			);
		}
	}

	private initForm(): void {
		this.phoneTypeFormGroup = this.fb.group({
			phoneType: [this.phoneType],
			phone: ['', [Validators.pattern('[A-Za-z0-9\-\_ ()]+')]],
		});

		this.isDisabled
			? this.phoneTypeFormGroup?.disable()
			: this.phoneTypeFormGroup?.enable();
	}

	// this function will get the available country codes from the server if they haven't already been retrieved.
	async loadAvailableCountryCodes(): Promise<CountryAvailableSms> {
		if (!this.countryAvailableSms) {
			this.countryAvailableSms =
				await this.apiService.getCountriesAvailableForSms();
		}

		// this is the list of country codes for the phone number country code picker
		this.countryCodes = this.countryAvailableSms.countryAvailableSms;

		return this.countryAvailableSms;
	}

	// this function will set the selectedCountryCode and phoneCode variables and make sure it only gets set to one of
	// the countries that we support
	async changeSelectedCountryCode(value: string | undefined): Promise<void> {
		// this will get the available country codes from the server if we don't already have them
		await this.loadAvailableCountryCodes();

		if (value && this.countryCodes.includes(value)) {
			this.selectedCountryCode = value;
		} else if (value === 'uk') {
			// the country code picker uses "gb" instead of "uk"
			this.selectedCountryCode = 'gb';
		} else {
			this.selectedCountryCode = 'us';
		}

		this.phoneCode = this.geoService.findCountryCodeByTwoLetterAbbreviation(
			this.selectedCountryCode
		);
		// this.changeDetector.detectChanges();
		this.updateParent();
	}

	onPhoneNumberChange(value: string): void {
		this.phoneNumber = value;
		this.updateParent();
	}

	updateParent(): void {
		let rawNum; // don't initialize this to anything. We want to return undefined if there isn't anything in the phoneNumber
		if (this.phoneNumber.replace(/\D/g, '') !== '') {
			rawNum = this.phoneCode + this.phoneNumber?.replace(/\D/g, '');
		}

		// I'm adding this if statement to fix an ExpressionChangedAfterItHasBeenCheckedError
		if (this.selectedCountryCode !== '') {
			this.userPhoneNumber.emit({
				countryCode: this.selectedCountryCode,
				rawPhoneNumber: rawNum,
			});
		}
	}

	//! Added blur event to emit the phone number when the user leaves the input field using flagForDisplayErrorMessage to emit only when required with onBlurFlag from here only :
	onBlurClick(): void {
		this.userPhoneNumber.emit({
			countryCode: this.selectedCountryCode,
			rawPhoneNumber: this.phoneCode + this.phoneNumber?.replace(/\D/g, ''),
			onBlurFlag: this.phoneNumber ? false : true,
		});
	}

	async ngOnChanges(): Promise<void> {
		if (this.flagForDisplayErrorMessage) {
			this.phoneTypeFormGroup.controls['phone'].addValidators([
				Validators.required,
			]);
			this.phoneTypeFormGroup.controls['phone'].updateValueAndValidity();
		} else {
			this.phoneTypeFormGroup.controls['phone'].removeValidators([
				Validators.required,
			]);
			this.phoneTypeFormGroup.controls['phone'].updateValueAndValidity();
		}

		if (
			this.existingPhoneNumber?.countryCode ||
			this.existingPhoneNumber?.rawPhoneNumber
		) {
			// this will get the available country codes from the server
			await this.loadAvailableCountryCodes();

			// if the country code already matches the selected country code we don't need to do set it again
			if (this.existingPhoneNumber?.countryCode !== this.selectedCountryCode) {
				// if we pass in a country code that should be the selected country code
				await this.changeSelectedCountryCode(
					this.existingPhoneNumber?.countryCode
				);
			}

			const newPhoneCode =
				this.geoService.findCountryCodeByTwoLetterAbbreviation(
					this.selectedCountryCode
				);

			// only update the phone code if the value has actually changed to avoid infinite updates between the parent
			// and child components
			if (this.phoneCode !== newPhoneCode) {
				this.phoneCode = newPhoneCode;
			}

			this.isDisabled
				? this.phoneTypeFormGroup?.disable()
				: this.phoneTypeFormGroup?.enable();

			// only update the phone number if we have a phone number
			if (this.existingPhoneNumber?.rawPhoneNumber) {
				// return raw phone number with spaces and special characters removed
				let newPhoneNumber = this.existingPhoneNumber.rawPhoneNumber.replace(
					/\D/g,
					''
				);

				// remove country code from the raw phone number value if it exists
				if (newPhoneNumber.startsWith(this.phoneCode)) {
					newPhoneNumber = newPhoneNumber.substring(this.phoneCode.length);
				}

				// only update the phone number if the value has actually changed to avoid infinite updates between the parent
				// and child components
				if (this.phoneNumber !== newPhoneNumber) {
					this.phoneNumber = newPhoneNumber;
				}
			}
		} else {
			this.isDisabled
				? this.phoneTypeFormGroup?.disable()
				: this.phoneTypeFormGroup?.enable();
			this.phoneNumber = '';
			this.phoneCode = '';

			if (this.existingPhoneNumber?.countryCode !== this.selectedCountryCode) {
				await this.changeSelectedCountryCode(
					this.existingPhoneNumber?.countryCode
				);
			}
		}
	}
}
